import type { User } from 'oidc-client-ts';

/**
 * The Cognito group that gates the entire admin backoffice. A signed-in user whose token does NOT
 * carry this group in `cognito:groups` sees the "Not authorized" page instead of the app.
 */
export const ADMIN_GROUP = 'admin';

/** Pull `cognito:groups` off either the id-token profile or the decoded access-token payload. */
export function groupsFromUser(user: User | null | undefined): string[] {
  if (!user) return [];
  // Cognito puts groups on the id-token (surfaced via `profile`) AND the access-token. Prefer the
  // profile claim; fall back to decoding the access token so we work regardless of which token the
  // group ends up on for a given pool/app-client config.
  const fromProfile = normalizeGroups((user.profile as Record<string, unknown>)['cognito:groups']);
  if (fromProfile.length > 0) return fromProfile;
  return normalizeGroups(decodeJwtClaim(user.access_token, 'cognito:groups'));
}

/** True when the user is a member of the admin group. */
export function isAdmin(user: User | null | undefined): boolean {
  return groupsFromUser(user).includes(ADMIN_GROUP);
}

/**
 * The AI supervisor group (compliance plan D3): one named person in the admin pool, the owner as the
 * interim member. It adds to `admin`, never replaces it: a supervisor who is not also an admin sees the
 * Not-authorized page like anyone else. In the Compliance console the group unlocks the supervision
 * acts only (D14: resolving an AI-content alert, recording the monthly escalation); it is a UI gate,
 * and the backend's `SupervisorOnly` policy is the boundary.
 */
export const SUPERVISOR_GROUP = 'ai-supervisor';

/** True when the user is a member of the AI supervisor group (see {@link SUPERVISOR_GROUP}). */
export function isSupervisor(user: User | null | undefined): boolean {
  return groupsFromUser(user).includes(SUPERVISOR_GROUP);
}

/** Best-effort display email for the header (id-token email → username → preferred_username). */
export function emailFromUser(user: User | null | undefined): string {
  const p = user?.profile as Record<string, unknown> | undefined;
  return (
    (p?.['email'] as string | undefined) ??
    (p?.['cognito:username'] as string | undefined) ??
    (p?.['preferred_username'] as string | undefined) ??
    'Signed in'
  );
}

function normalizeGroups(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string' && value.trim()) return [value];
  return [];
}

/** Decode a single claim from a JWT's payload without verifying the signature (UI gate only). */
function decodeJwtClaim(token: string | undefined, claim: string): unknown {
  if (!token) return undefined;
  const part = token.split('.')[1];
  if (!part) return undefined;
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as Record<string, unknown>;
    return payload[claim];
  } catch {
    return undefined;
  }
}
